import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import AgentAnnonceService from "services/agentAnnonceService";

// Async thunks for login and logout
export const getAll = createAsyncThunk(
  "agent/announcements",
  async ({ page = 1, searchValue = null }, { rejectWithValue }) => {
    try {
      const response = await AgentAnnonceService.getAll(page, searchValue);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getById = createAsyncThunk(
  "agent/announcement/getById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await AgentAnnonceService.getById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createAnnouncement = createAsyncThunk(
  "agent/announcement/create",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await AgentAnnonceService.create(payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateAnnouncement = createAsyncThunk(
  "agent/announcement/update",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const response = await AgentAnnonceService.update(id, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const deleteAnnouncement = createAsyncThunk(
  "agent/announcement/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await AgentAnnonceService.delete(id);
      return { id, response: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Initial state
const initialState = {
  agentAnnonces: [],
  agentLoading: false,
  agentError: null,
  agentTotalItems: 0,
  agentCurrentPage: 1,
  agentTotalPages: 0,
  agentFetchLoading: false,
  selectedAnnouncement: null,
  selectedLoading: false,
  selectedError: null,
  createLoading: false,
  createError: null,
  createSuccess: false,
  updateLoading: false,
  updateError: null,
  updateSuccess: false,
  deleteLoading: false,
  deleteError: null,
  deleteSuccess: false,
};

// Slice
const agentAnnonceSlice = createSlice({
  name: "agentAnnonce",
  initialState,
  reducers: {
    setCurrentPage: (state, action) => {
      state.agentCurrentPage = action.payload;
    },
    resetCreateStatus: (state) => {
      state.createSuccess = false;
      state.createError = null;
    },
    resetUpdateStatus: (state) => {
      state.updateSuccess = false;
      state.updateError = null;
    },
    resetDeleteStatus: (state) => {
      state.deleteSuccess = false;
      state.deleteError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAll.pending, (state) => {
        state.agentLoading = true;
        state.agentError = null;
      })
      .addCase(getAll.fulfilled, (state, action) => {
        state.agentLoading = false;
        state.agentAnnonces = action.payload.announcements;
        state.agentTotalItems = action.payload.totalAnnouncements;
        state.agentTotalPages = action.payload.totalPages;
      })
      .addCase(getAll.rejected, (state, action) => {
        state.agentAnnonces = [];
        state.agentLoading = false;
        state.agentError = action.payload;
      })
      .addCase(getById.pending, (state) => {
        state.selectedLoading = true;
        state.selectedError = null;
      })
      .addCase(getById.fulfilled, (state, action) => {
        state.selectedLoading = false;
        state.selectedAnnouncement = action.payload;
      })
      .addCase(getById.rejected, (state, action) => {
        state.selectedLoading = false;
        state.selectedAnnouncement = null;
        state.selectedError = action.payload;
      })
      .addCase(createAnnouncement.pending, (state) => {
        state.createLoading = true;
        state.createError = null;
        state.createSuccess = false;
      })
      .addCase(createAnnouncement.fulfilled, (state, action) => {
        state.createLoading = false;
        state.createSuccess = true;
      })
      .addCase(createAnnouncement.rejected, (state, action) => {
        state.createLoading = false;
        state.createError = action.payload;
        state.createSuccess = false;
      })
      .addCase(updateAnnouncement.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(updateAnnouncement.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.updateSuccess = true;
        if (state.agentAnnonces.length > 0) {
          const index = state.agentAnnonces.findIndex(
            (a) => a._id === action.payload._id
          );
          if (index !== -1) {
            state.agentAnnonces[index] = action.payload;
          }
        }
        if (
          state.selectedAnnouncement &&
          state.selectedAnnouncement._id === action.payload._id
        ) {
          state.selectedAnnouncement = action.payload;
        }
      })
      .addCase(updateAnnouncement.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload;
        state.updateSuccess = false;
      })
      .addCase(deleteAnnouncement.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
        state.deleteSuccess = false;
      })
      .addCase(deleteAnnouncement.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.deleteSuccess = true;
        state.agentAnnonces = state.agentAnnonces.filter(
          (a) => a._id !== action.payload.id
        );
        if (
          state.selectedAnnouncement &&
          state.selectedAnnouncement._id === action.payload.id
        ) {
          state.selectedAnnouncement = null;
        }
      })
      .addCase(deleteAnnouncement.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload;
        state.deleteSuccess = false;
      });
  },
});
export const {
  setCurrentPage,
  resetCreateStatus,
  resetUpdateStatus,
  resetDeleteStatus,
} = agentAnnonceSlice.actions;
export default agentAnnonceSlice.reducer;
